// Getting from one internal screen to the other.
//
// `/tuner`, `/features` and `/users` are behind one door and were dead ends:
// the only way from any of them to another was Close, back to the game, and
// then the address bar. Screens for one person with no way between them is a
// menu the person has to keep in their head.
//
// **One list, so a fourth screen appears everywhere by being added once.** The
// pages differ in everything else — one is a live feed, one a configuration
// table, one a queue of people — and what they share is exactly this: who is
// allowed in, and where else that lets them go.

import { Btn, styled, Text, TOKENS } from "./kit";

/** Every screen behind the tuner gate. Add one here and every header gains it. */
export const ADMIN_PAGES = [
  { id: "tuner", path: "/tuner", name: "Tuning" },
  { id: "features", path: "/features", name: "Features" },
  { id: "users", path: "/users", name: "Testers" }
] as const;

export type AdminPage = (typeof ADMIN_PAGES)[number]["id"];

/**
 * The bar the three screens hang their header off.
 *
 * `TOKENS.layout.adminBar` is its height and is the one fixed layout number the
 * kit still carries: `/tuner` and the feedback board inset a *camera* by it, so
 * it has to be a number the render layer can read rather than a measurement.
 * Sticky, so scrolling a long feed never loses the way back.
 */
export const AdminBar = styled("div")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: theme.kit.z.chrome,
  boxSizing: "border-box",
  minHeight: TOKENS.layout.adminBar,
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  flexWrap: "wrap",
  padding: `0 ${theme.kit.space.xl}px`,
  background: `color-mix(in srgb, ${theme.kit.color.panel} 92%, transparent)`,
  backdropFilter: "blur(6px)",
  borderBottom: `2px solid ${theme.kit.color.line}`
}));

const Row = styled("nav")(({ theme }) => ({
  display: "flex",
  alignItems: "baseline",
  gap: theme.kit.space.md
}));

/**
 * The other screens, as buttons that are really links.
 *
 * `withComponent` rather than `as`, so the props typecheck as an anchor's:
 * `href` is the half that lets the link be copied, opened in a tab and read by
 * a screen reader as a link, and `Btn`'s own props stay where they were.
 */
const NavLink = Btn.withComponent("a");

/** The screen you are on. A link to where you already are does nothing. */
const Here = styled("h1")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 20,
  lineHeight: 1.1,
  color: theme.kit.color.ink
}));

/**
 * The crosslinked header, as the page's own title.
 *
 * **`onGo` navigates in place**; the `href` is real so the link can be copied,
 * opened in a tab and read by a screen reader as the link it is. Both, because
 * one without the other is either a page that reloads a 300KB digest to move
 * two inches, or something that looks like a link and cannot be treated as one.
 */
export function AdminNav({
  current,
  onGo
}: {
  current: AdminPage;
  onGo: (page: AdminPage) => void;
}): React.ReactElement {
  return (
    <Row>
      {ADMIN_PAGES.map((page) =>
        page.id === current ? (
          <Here key={page.id} aria-current="page">
            {page.name}
          </Here>
        ) : (
          <NavLink
            key={page.id}
            variant="ghost"
            small
            href={page.path}
            data-testid={`admin-${page.id}`}
            onClick={(event: React.MouseEvent) => {
              // **Let a modified click be a modified click.** Swallowing
              // ctrl/cmd-click would take "open the other screen in a tab" away
              // from a page whose whole job is being looked at beside another.
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              onGo(page.id);
            }}
          >
            <Text tone="dim" weight={700} size={13}>
              {page.name}
            </Text>
          </NavLink>
        )
      )}
    </Row>
  );
}
